import { SpotifyTimeSeriesRequestParams, SpotifyTimeSeriesResponse } from '@round/api';
import { useCallback } from 'react';
import { useSong, useMusicSong } from '@round/creatorbase-helpers/api-hooks';
import { useSpotifyTimeSeries } from '@round/platforms/spotify';

type Params = Pick<SpotifyTimeSeriesRequestParams, 'start_date' | 'end_date'> & {
    //creatorbase song id
    songId: number | null | undefined;
};

export function useSpotifyPopularityChart({ start_date, end_date, songId }: Params) {
    const { data: songData, isLoading: isSongDataLoading, isError: isSongDataError } = useSong(songId);

    const {
        data: musicSongData,
        isLoading: isMusicSongDataLoading,
        isError: isMusicSongDataError,
    } = useMusicSong(songData?.music_song_id, { enabled: !!songData?.music_song_id });

    const spotifySongId = musicSongData?.spotify_id;

    const { data, isError, isLoading } = useSpotifyTimeSeries(
        {
            type: 'song',
            fields: ['popularity'],
            ids: spotifySongId ? [spotifySongId] : [],
            start_date,
            end_date,
        },
        {
            enabled: !!spotifySongId,
            select: useCallback(
                (data: SpotifyTimeSeriesResponse) =>
                    spotifySongId
                        ? {
                              data:
                                  data[spotifySongId]?.popularity.map((point) => ({
                                      x: point.timestamp,
                                      y: point.value,
                                  })) || [],
                              borderColor: '#1ED760',
                              label: 'Spotify Popularity',
                          }
                        : null,
                [spotifySongId]
            ),
        }
    );

    const hasData = !!data && data.data.length > 0;
    // Only show the chart once popularity data has been successfully fetched and has
    // points to plot - never a loading or error state, since we can't be sure the data
    // exists until it arrives.
    const shouldHideChart = songData?.status === 'unreleased' || !hasData;

    return {
        data,
        isError: isError || isSongDataError || isMusicSongDataError,
        isLoading: isLoading || isSongDataLoading || isMusicSongDataLoading,
        shouldHideChart,
    };
}
